import React, { useContext, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../shared/ui/button';
import { linksForKnowledgeBase } from '../helpers/header/links_for_knowledge_base';
import { useResize } from '../hooks/use_resize';
import CampSelect from './camp_select';
import { Modal } from './common/modal';
import { Popup } from './common/popup';
import { USER_CONTEXT, UserContext } from './contexts/user_context';
import HeaderLinks from './header_links';
import Account from './icons/account';
import BellCogOutline from './icons/bell_cog_outline';
import Help from './icons/help';
import Knowledge from './icons/knowledge';
import Logout from './icons/logout';
import MenuIcon from './icons/menu_icon';
import { Menu } from './menu';

export const HEADER_HEIGHT = 56;

export default function Header({
  showSidebar,
  onOpenSidebar,
  isRegistration,
}: {
  showSidebar?: boolean;
  onOpenSidebar?: () => void;
  isRegistration?: boolean;
}) {
  const helpRef = useRef(null);
  const accountRef = useRef(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const userContext = useContext<USER_CONTEXT>(UserContext);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const { isExtraLargeScreen } = useResize();
  const navigate = useNavigate();
  const noSidebar = userContext.current_camp?.has_pending_contract || isRegistration;
  const pathname = useLocation().pathname;

  const knowledgeBaseLink = linksForKnowledgeBase.get(pathname);

  const userHasActiveCamps = userContext.camps?.some((camp) => !camp.has_pending_contract);

  const handleClickMainLogo = () => {
    if (isRegistration) {
      window.location.href = 'https://ubook.ru/';
    } else if (userContext.sign_in_url) {
      window.location.href = userContext.sign_in_url;
    } else {
      navigate('/');
    }
  };

  return (
    <div className={`header ${!showSidebar && 'header-full_width'} ${noSidebar && 'header-no_sidebar'}`}>
      {(isExtraLargeScreen || noSidebar) && (
        <div onClick={handleClickMainLogo} className={`sidebar__logo_wrapper header__logo ${!noSidebar && 'show-xl'}`}>
          <div className={'sidebar__logo'} />
        </div>
      )}
      {!isExtraLargeScreen && !showSidebar && !noSidebar && (
        <Button
          variant={'outline'}
          className={'sidebar__btn'}
          onClick={onOpenSidebar}
          title={'Показать меню'}
          icon={<MenuIcon />}
        />
      )}
      {(!noSidebar || userHasActiveCamps) && (
        <div className={'hide-md'}>
          <CampSelect />
        </div>
      )}
      {!noSidebar && (
        <div className={'hide-xl'}>
          <HeaderLinks />
        </div>
      )}
      <div style={{ flex: 1 }} />
      <div className={'flex_horizontal'}>
        <Button btnRef={helpRef} onClick={() => setShowHelp(true)} icon={<Help />} />
        <Popup
          anchor={helpRef.current}
          title={'Поддержка'}
          open={showHelp}
          id={'help_popup'}
          onClose={() => setShowHelp(false)}
        >
          <div className={'header_help__wrapper'}>
            <div className={'typography-regular'}>Есть вопросы?</div>
            <div className={'typography-light margin-top-12'}>Звоните:</div>
            <a href={'tel:+7 (937) 701-6063'} className={'typography-link'}>
              +7 (937) 701-6063
            </a>
            <div className={'typography-light margin-top-8'}>Или пишите:</div>
            <a
              href={isRegistration ? 'mailto:partner@ubook.ru' : 'mailto:content@ubook.ru'}
              className={'typography-link'}
            >
              {isRegistration ? 'partner@ubook.ru' : 'content@ubook.ru'}
            </a>
          </div>
          {!isRegistration && (
            <Button
              className={'header__btn-knowledge-base'}
              href={knowledgeBaseLink}
              target={'_blank'}
              icon={<Knowledge color={'#000000CC'} size={20} />}
            >
              База знаний
            </Button>
          )}
        </Popup>
        {userContext.current_user?.id ? (
          <>
            <Button
              btnRef={accountRef}
              onClick={() => setShowAccount(true)}
              icon={<Account />}
              dropdown={!isExtraLargeScreen}
              opened={showAccount}
            >
              {!isExtraLargeScreen && userContext.current_user.name}
            </Button>
            <Popup
              className={'account-popup'}
              anchor={accountRef.current}
              open={showAccount}
              id={'account_popup'}
              title={userContext.current_user.name}
              onClose={() => setShowAccount(false)}
            >
              <Button
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => setShowAccount(false)}
                href={'/notifications_settings'}
                className={'align-left'}
                icon={<BellCogOutline />}
              >
                Настройка уведомлений
              </Button>
              <Button
                style={{ whiteSpace: 'nowrap' }}
                href={'/logout'}
                native
                className={'align-left'}
                icon={<Logout />}
              >
                Выйти
              </Button>
            </Popup>
          </>
        ) : (
          <Button href={userContext.sign_in_url} native icon={<Account />}>
            <span className={'hide-xl'}>Войти</span>
          </Button>
        )}
        {!noSidebar && (
          <>
            <Button className={'show-xl'} onClick={() => setOpenMobileMenu(true)} icon={<MenuIcon />} />
            <Modal
              className={'modal-no_padding'}
              onClose={() => setOpenMobileMenu(false)}
              open={openMobileMenu}
              title={<CampSelect />}
            >
              <Menu onGoTo={() => setOpenMobileMenu(false)} />
            </Modal>
          </>
        )}
      </div>
    </div>
  );
}
